// ----------------------------------------------------------------------------
// Section: Featured Collections
// ----------------------------------------------------------------------------

import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Keyboard } from 'swiper/modules';

// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import './_featured-collections.scss';

// -------------------------------------
// Constants
// -------------------------------------

var SECTION_SELECTOR = '.featured-collections';
var CAROUSEL_SELECTOR = '.carousel--featured-collections';
var PREV_BUTTON_SELECTOR = '.featured-collections__arrow--prev';
var NEXT_BUTTON_SELECTOR = '.featured-collections__arrow--next';
var PAGINATION_SELECTOR = '.featured-collections__dots';

// Matches $container--laptop (1080px) so the swiper breakpoint and the
// CSS breakpoint that reveals the arrow controls (@include bp(laptop)) agree.
var DESKTOP_BREAKPOINT = 1080;
var MOBILE_SLIDES_PER_VIEW = 2.1;
var DESKTOP_SLIDES_PER_VIEW = 3.75;
var instances = new Map();
var instanceIndex = 0;

// -------------------------------------
// Helpers
// -------------------------------------

function updateBullets(paginationSelector, snapIndex) {
  document.querySelectorAll("".concat(paginationSelector, " .swiper-pagination-bullet")).forEach((bullet, i) => {
    if (i === snapIndex) {
      bullet.setAttribute('aria-current', 'true');
    } else {
      bullet.removeAttribute('aria-current');
    }
  });
}
function applyAutoRatio(sectionEl, carousel) {
  var firstImg = carousel.querySelector('.card--featured-collection__image');
  if (!firstImg) return;
  var apply = () => {
    if (firstImg.naturalWidth && firstImg.naturalHeight) {
      sectionEl.style.setProperty('--featured-collections-aspect-ratio', "".concat(firstImg.naturalWidth, " / ").concat(firstImg.naturalHeight));
    }
  };
  if (firstImg.complete) {
    apply();
  } else {
    firstImg.addEventListener('load', apply, {
      once: true
    });
  }
}

// -------------------------------------
// Setup
// -------------------------------------

function setupSection(sectionEl) {
  var _sectionEl$querySelec, _sectionEl$querySelec2, _sectionEl$querySelec3;
  var carousel = sectionEl.querySelector(CAROUSEL_SELECTOR);
  if (!carousel) return;
  if (sectionEl.dataset.autoRatio === 'true') {
    applyAutoRatio(sectionEl, carousel);
  }
  var index = instanceIndex++;
  var uniqueCarouselClass = "carousel--featured-collections-".concat(index);
  var uniquePrevClass = "featured-collections-btn-prev-".concat(index);
  var uniqueNextClass = "featured-collections-btn-next-".concat(index);
  var uniquePaginationClass = "featured-collections-pagination-".concat(index);
  carousel.classList.add(uniqueCarouselClass);
  (_sectionEl$querySelec = sectionEl.querySelector(PREV_BUTTON_SELECTOR)) === null || _sectionEl$querySelec === void 0 || _sectionEl$querySelec.classList.add(uniquePrevClass);
  (_sectionEl$querySelec2 = sectionEl.querySelector(NEXT_BUTTON_SELECTOR)) === null || _sectionEl$querySelec2 === void 0 || _sectionEl$querySelec2.classList.add(uniqueNextClass);
  (_sectionEl$querySelec3 = sectionEl.querySelector(PAGINATION_SELECTOR)) === null || _sectionEl$querySelec3 === void 0 || _sectionEl$querySelec3.classList.add(uniquePaginationClass);
  var state = {
    index,
    carouselSelector: ".".concat(uniqueCarouselClass),
    prevSelector: ".".concat(uniquePrevClass),
    nextSelector: ".".concat(uniqueNextClass),
    paginationSelector: ".".concat(uniquePaginationClass),
    swiper: null
  };
  instances.set(sectionEl, state);
  initSwiper(sectionEl, state);
}
function initSwiper(sectionEl, state) {
  state.swiper = new Swiper(state.carouselSelector, {
    modules: [Navigation, Pagination, A11y, Keyboard],
    slidesPerView: MOBILE_SLIDES_PER_VIEW,
    slidesPerGroup: 1,
    spaceBetween: 0,
    watchOverflow: true,
    keyboard: true,
    observer: true,
    observeParents: true,
    a11y: {
      slideRole: null
    },
    breakpoints: {
      [DESKTOP_BREAKPOINT]: {
        slidesPerView: DESKTOP_SLIDES_PER_VIEW,
        spaceBetween: 0
      }
    },
    navigation: {
      nextEl: state.nextSelector,
      prevEl: state.prevSelector
    },
    pagination: {
      el: state.paginationSelector,
      clickable: true,
      renderBullet(_index, className) {
        return "<span role=\"button\" tabindex=\"0\" class=\"".concat(className, "\"></span>");
      }
    },
    on: {
      init(swiperInstance) {
        sectionEl.classList.toggle('featured-collections--static', swiperInstance.isLocked);
        updateBullets(state.paginationSelector, swiperInstance.snapIndex || 0);
        sectionEl.querySelectorAll("".concat(state.paginationSelector, " .swiper-pagination-bullet")).forEach((bullet, i) => {
          bullet.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              swiperInstance.slideTo(i);
            }
          });
        });
      },
      lock() {
        sectionEl.classList.add('featured-collections--static');
      },
      unlock() {
        sectionEl.classList.remove('featured-collections--static');
      },
      snapIndexChange(swiperInstance) {
        updateBullets(state.paginationSelector, swiperInstance.snapIndex || 0);
      }
    }
  });
}
function initFeaturedCollections() {
  var container = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  container.querySelectorAll(SECTION_SELECTOR).forEach(sectionEl => setupSection(sectionEl));
}
initFeaturedCollections();
document.addEventListener('shopify:section:load', _ref => {
  var {
    target
  } = _ref;
  initFeaturedCollections(target);
});